/**
 * CW-SF-R2 TKT-6 — Large Deals Needing Attention (CEO role).
 *
 * Open opps Amount > $500K AND late-stage (Commercial / Closing),
 * sorted by CloseDate asc. The exec-sponsorship surface.
 */
import { useCallback } from 'react';
import { ExternalLink, AlertCircle } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface LargeDeal {
  id: string;
  external_file_id: string;
  name: string;
  web_url: string | null;
  stage: string | null;
  amount: number | null;
  close_date: string | null;
  owner_id: string | null;
  owner_name: string | null;
}
interface ApiResponse extends SfWidgetEnvelope {
  items: LargeDeal[];
  amount_min: number;
  late_stages: string[];
}

const REFRESH_S = 5 * 60;

export function SfLargeDealsAttentionWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/large-deals-attention', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  const items = data?.items ?? [];

  return (
    <div className="sf-widget sf-widget--large-deals">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={items.length > 0}
        emptyMessage={
          `No open deals over ${formatCurrency(data?.amount_min ?? 500_000)} in late stages.`
        }
      >
        <ul className="sf-widget__list">
          {items.map((d) => (
            <li key={d.id} className="sf-widget__row">
              <a
                href={d.web_url ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="sf-widget__row-name"
              >
                <AlertCircle size={10} />
                {d.name}
                <ExternalLink size={10} />
              </a>
              <div className="sf-widget__row-chips">
                {d.stage && <span className="sf-widget__chip">{d.stage}</span>}
                {d.amount !== null && (
                  <span className="sf-widget__chip sf-widget__chip--amount">
                    {formatCurrency(d.amount)}
                  </span>
                )}
                {d.owner_name && (
                  <span className="sf-widget__chip sf-widget__chip--owner">
                    {d.owner_name}
                  </span>
                )}
                {d.close_date && (
                  <span className="sf-widget__chip">{d.close_date}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </SfWidgetShell>
    </div>
  );
}
